import { useState } from "react";
import { Link } from "react-router-dom";
import { useNewsletterSubscribe } from "../lib/useNewsletterSubscribe";
import { useResultEmail } from "../lib/useResultEmail";

export const OperationalClarityResultEmail = ({ profile }) => {
  const [email, setEmail] = useState("");
  const [subscribe, setSubscribe] = useState(false);
  const [website, setWebsite] = useState("");
  const [newsletterOutcome, setNewsletterOutcome] = useState("not-requested");
  const resultEmail = useResultEmail(profile);
  const newsletter = useNewsletterSubscribe();
  const isSending = resultEmail.state === "sending" || newsletter.state === "sending";

  const clearError = () => {
    if (resultEmail.state === "error") resultEmail.reset();
    setNewsletterOutcome("not-requested");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setNewsletterOutcome("not-requested");
    const result = await resultEmail.submit({ email, website });
    if (!result.ok || !subscribe) return;

    const subscription = await newsletter.submit({ email, consent: true, website });
    setNewsletterOutcome(subscription.ok ? "success" : "error");
  };

  return (
    <section
      data-testid="result-email-section"
      className="mt-12 border-y border-rule py-10"
      aria-labelledby="result-email-heading"
    >
      <h2 id="result-email-heading" className="font-display text-2xl tracking-tight">
        Email my result
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-mute">
        Send a copy of this Operational Clarity Review result to your inbox.
      </p>

      {resultEmail.state === "success" ? (
        <div className="mt-6" role="status" aria-live="polite" data-testid="result-email-success">
          {newsletterOutcome === "success" ? (
            <p className="font-semibold text-ink" data-testid="result-newsletter-success">
              Your result has been emailed. Check your inbox to confirm your Operational Brief subscription.
            </p>
          ) : newsletterOutcome === "error" ? (
            <p className="font-semibold text-terra" data-testid="result-newsletter-error">
              Your result has been emailed, but we couldn't start the newsletter subscription. You can try subscribing again separately.
            </p>
          ) : (
            <p className="font-semibold text-ink">Your result has been emailed to you.</p>
          )}
        </div>
      ) : (
        <form className="relative mt-6" onSubmit={onSubmit} noValidate data-testid="result-email-form">
          <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
            <label htmlFor="result-email-address" className="sr-only">Email address</label>
            <input
              id="result-email-address"
              data-testid="result-email-address"
              type="email"
              autoComplete="email"
              placeholder="Email address"
              value={email}
              onChange={(event) => { clearError(); setEmail(event.target.value); }}
              disabled={isSending}
              aria-invalid={resultEmail.state === "error"}
              aria-describedby={resultEmail.state === "error" ? "result-email-error" : undefined}
              className="flex-1 bg-transparent border-b border-ink/25 outline-none py-3 text-base placeholder:text-mute/50 disabled:opacity-50 focus:border-ink transition-colors"
            />
            <button
              type="submit"
              data-testid="result-email-submit"
              disabled={isSending}
              className="inline-flex items-center justify-center bg-ink text-paper text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-terra transition-colors disabled:opacity-60"
            >
              {isSending ? "Sending..." : "Email my result"}
            </button>
          </div>

          <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
            <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} /></label>
          </div>

          <label className="mt-5 flex items-start gap-3 max-w-xl text-sm text-mute cursor-pointer">
            <input
              type="checkbox"
              data-testid="result-newsletter-consent"
              className="mt-1 accent-[#C8553D]"
              checked={subscribe}
              onChange={(event) => { clearError(); setSubscribe(event.target.checked); }}
              disabled={isSending}
            />
            <span>
              Also subscribe me to The Operational Brief
              <span className="mt-1 block text-xs leading-relaxed">
                Optional. We’ll send a separate email asking you to confirm the subscription, and you can unsubscribe at any time.
              </span>
            </span>
          </label>

          <p className="mt-5 max-w-2xl text-xs leading-relaxed text-mute">
            We’ll use your email address and the derived result shown above to send this copy. We do not send your individual answers or written example. This does not subscribe you to marketing unless you select the separate checkbox. Read our{" "}
            <Link to="/privacy-policy" className="underline underline-offset-2 link-rule">Privacy Policy</Link>.
          </p>

          {resultEmail.state === "error" && (
            <p id="result-email-error" data-testid="result-email-error" role="alert" className="mt-4 text-sm font-medium text-terra">
              {resultEmail.error}
            </p>
          )}
        </form>
      )}
    </section>
  );
};
